import { zeroAddress, type Address } from 'viem'
import type { VaultInfoFull, LTVInfo, Cluster } from './types.js'

/**
 * Returns a set of collateral vault addresses that have active (non-zero)
 * borrowLTV. The LTV array is append-only on-chain, so disabled collaterals
 * appear with borrowLTV = 0.
 */
function getActiveCollaterals(ltvInfo: readonly LTVInfo[]): Set<string> {
  const active = new Set<string>()
  for (const entry of ltvInfo ?? []) {
    if (entry.borrowLTV > 0n) {
      active.add(entry.collateral.toLowerCase())
    }
  }
  return active
}

/** Case-insensitive lookup in the vault data map. */
function findVaultInfo(
  normalizedAddr: string,
  vaultData: Map<Address, VaultInfoFull>,
): VaultInfoFull | undefined {
  for (const [key, value] of vaultData) {
    if (key.toLowerCase() === normalizedAddr) return value
  }
  return undefined
}

/**
 * Builds a bidirectional adjacency list from all vaults' collateralLTVInfo.
 *
 * If vault A lists vault B as collateral (borrowLTV > 0), we add edges
 * A→B and B→A. This captures:
 *  - Forward: "A accepts B as collateral"
 *  - Reverse: "B is accepted by A" (B might not know about A from its own config)
 *
 * Only edges between known vaults (in the input set) are included.
 */
function buildAdjacencyList(
  vaultData: Map<Address, VaultInfoFull>,
): Map<string, Set<string>> {
  const adj = new Map<string, Set<string>>()
  const knownVaults = new Set<string>()

  // Register all known vaults
  for (const addr of vaultData.keys()) {
    const norm = addr.toLowerCase()
    knownVaults.add(norm)
    adj.set(norm, new Set())
  }

  // Add bidirectional edges from collateralLTVInfo
  for (const [addr, info] of vaultData) {
    const from = addr.toLowerCase()
    const activeCollaterals = getActiveCollaterals(info.collateralLTVInfo)

    for (const to of activeCollaterals) {
      if (knownVaults.has(to) && from !== to) {
        adj.get(from)!.add(to)
        adj.get(to)!.add(from)
      }
    }
  }

  return adj
}

/**
 * BFS from a starting vault to find all transitively connected vaults.
 */
function bfs(
  start: string,
  adj: Map<string, Set<string>>,
  visited: Set<string>,
): string[] {
  const component: string[] = []
  const queue: string[] = [start]
  visited.add(start)

  while (queue.length > 0) {
    const current = queue.shift()!
    component.push(current)

    const neighbors = adj.get(current)
    if (!neighbors) continue

    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        visited.add(neighbor)
        queue.push(neighbor)
      }
    }
  }

  return component
}

/**
 * Finds all connected components in the bidirectional collateral graph.
 */
function findConnectedComponents(adj: Map<string, Set<string>>): string[][] {
  const visited = new Set<string>()
  const components: string[][] = []

  for (const vault of adj.keys()) {
    if (!visited.has(vault)) {
      const component = bfs(vault, adj, visited)
      components.push(component)
    }
  }

  return components
}

/**
 * Derives the majority governor from a set of vaults.
 * Returns the most common non-zero governorAdmin, or zero address if
 * all vaults are ungoverned.
 */
function deriveGovernor(
  vaultAddresses: string[],
  vaultData: Map<Address, VaultInfoFull>,
): Address {
  const counts = new Map<string, number>()
  const zeroNorm = zeroAddress

  for (const addr of vaultAddresses) {
    const info = findVaultInfo(addr, vaultData)
    if (!info) continue
    const gov = info.governorAdmin?.toLowerCase()
    if (!gov || gov === zeroNorm) continue
    counts.set(gov, (counts.get(gov) ?? 0) + 1)
  }

  let best = zeroAddress as Address
  let bestCount = 0
  for (const [gov, count] of counts) {
    if (count > bestCount) {
      best = gov as Address
      bestCount = count
    }
  }
  return best
}

/**
 * Derives clusters from vault data using pure graph traversal.
 *
 * Algorithm:
 * 1. Build a bidirectional adjacency list from all active collateralLTVInfo
 *    entries. If vault A lists vault B (borrowLTV > 0), edges A↔B are added.
 * 2. BFS from each unvisited vault to find connected components.
 * 3. Each connected component = one cluster (cross-margin pool).
 * 4. Governor is derived as metadata (majority non-zero governorAdmin).
 *
 * This does NOT pre-filter by governor. Two vaults with different governors
 * that reference each other will end up in the same cluster, which is
 * correct — the collateral graph defines the pool boundary, not governance.
 *
 * @param vaultData - map of vault address → VaultInfoFull
 * @param includeSingletons - include single-vault clusters (default false)
 */
export function deriveClusters(
  vaultData: Map<Address, VaultInfoFull>,
  includeSingletons: boolean = false,
): Cluster[] {
  if (vaultData.size === 0) return []

  const adj = buildAdjacencyList(vaultData)
  const components = findConnectedComponents(adj)
  const clusters: Cluster[] = []

  for (const component of components) {
    if (!includeSingletons && component.length < 2) continue

    // Check that the component has at least one active edge
    // (for singletons this is skipped; for multi-vault we verify
    // connectivity was through actual LTV references, not just
    // being in the dataset)
    if (!includeSingletons) {
      let hasActiveEdge = false
      for (const addr of component) {
        const neighbors = adj.get(addr)
        if (neighbors && neighbors.size > 0) {
          hasActiveEdge = true
          break
        }
      }
      if (!hasActiveEdge) continue
    }

    const clusterVaultData = new Map<Address, VaultInfoFull>()
    for (const addr of component) {
      const info = findVaultInfo(addr, vaultData)
      if (info) clusterVaultData.set(addr as Address, info)
    }

    clusters.push({
      governor: deriveGovernor(component, vaultData),
      vaults: component as Address[],
      vaultData: clusterVaultData,
    })
  }

  return clusters
}
